import { type Brand, make } from "ts-brand";
import { z } from "zod";

/**
 * ExternalFormLink domain model — one entry of the single, global «Linkliste zu
 * externen Formularen» (Scenario S11). A TrexpertAdmin maintains an ordered list
 * of named external-form links; every customer sees the identical list and opens
 * a form in a new browser tab via the stored URL. The portal stores only the URL
 * and display metadata, never the submitted form data.
 *
 * The editable surface stays plain data: `label` and `description` render as text
 * (auto-escaped), `icon` is a key from a curated allow-list mapped to a known
 * inline SVG on the client (never author-supplied markup), and `url` is validated
 * as an HTTPS URL. So nothing an admin types can ever be rendered as executable
 * markup. Nominal IDs via ts-brand; Zod validates at the boundary.
 */

export type ExternalFormLinkId = Brand<string, "ExternalFormLinkId">;
export const ExternalFormLinkId = make<ExternalFormLinkId>();

/**
 * Curated icon allow-list. Keys only — the client maps each key to a known inline
 * SVG ({@link FormLinkIcon} component). Never free SVG/HTML, so an admin can never
 * inject markup through the icon field.
 */
export const FORM_LINK_ICONS = [
    "user-plus", // Mitarbeitende anmelden
    "user-minus", // Mitarbeitende abmelden
    "wallet", // Lohn / Familienzulagen
    "home", // Adressänderung
    "file-text", // generisches Formular / Meldung
    "calendar", // Termin / Frist / Entschädigung
    "briefcase", // Firma / Mandat
    "mail", // Kontakt
    "clipboard", // Erfassung / Meldung
    "link", // Fallback / extern
] as const;
export type FormLinkIcon = (typeof FORM_LINK_ICONS)[number];
export const FormLinkIconSchema = z.enum(FORM_LINK_ICONS);
export const DEFAULT_FORM_LINK_ICON: FormLinkIcon = "file-text";

/** Target URL for the kmu-trexpert customer portal (default for all seeded links). */
export const KUNDENPORTAL_URL = "https://www.kmu-trexpert.ch/kundenportal";

/**
 * Elfsight Form-Builder app-id — the only identifier that varies between the
 * customer's embedded forms (all share one embed pattern). A link that carries
 * one opens as an in-portal popup (same-origin embed route); a link without one
 * keeps the classic external-link behaviour (fallback). Validated as a plain
 * UUID so it can never break out of the `elfsight-app-<id>` class attribute in
 * the embed page.
 */
export const FormEmbedAppIdSchema = z
    .string()
    .trim()
    .regex(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        "Bitte eine gültige Elfsight-App-ID angeben (UUID)."
    );

/** Validates that the stored URL is a well-formed HTTPS address. */
export const FormLinkUrlSchema = z
    .string()
    .trim()
    .url("Bitte eine gültige URL angeben (https://…).")
    .refine(url => url.startsWith("https://"), "Die URL muss mit https:// beginnen.");

export const ExternalFormLinkSchema = z.object({
    id: z.string().min(1).transform(ExternalFormLinkId),
    label: z.string().trim().min(1).max(80),
    url: FormLinkUrlSchema,
    description: z.string().trim().max(160).default(""),
    icon: FormLinkIconSchema,
    /** Optional Elfsight app-id; when set the link opens as an in-portal popup. */
    embedAppId: FormEmbedAppIdSchema.optional(),
    /**
     * The iframe `src` for {@link embedAppId}, computed at the read boundary and NEVER persisted —
     * it depends on where the module is deployed, not on the link. Absent when the link has no
     * `embedAppId`. The client uses it verbatim rather than building a URL it cannot get right.
     */
    embedUrl: z.string().optional(),
    /** Ascending sort key; densified to `0,1,2,…` on every mutation. */
    order: z.number().int().nonnegative(),
});
export type ExternalFormLink = z.infer<typeof ExternalFormLinkSchema>;

/** Boundary input for `createFormLink` — a TrexpertAdmin appends a new form. */
export const CreateFormLinkInputSchema = z.object({
    label: z.string().trim().min(1).max(80),
    url: FormLinkUrlSchema,
    description: z.string().trim().max(160).default(""),
    icon: FormLinkIconSchema.default(DEFAULT_FORM_LINK_ICON),
    embedAppId: FormEmbedAppIdSchema.optional(),
});
export type CreateFormLinkInput = z.infer<typeof CreateFormLinkInputSchema>;

/** Boundary input for `updateFormLink` — edit an existing form's fields. */
export const UpdateFormLinkInputSchema = z.object({
    id: z.string().min(1).transform(ExternalFormLinkId),
    label: z.string().trim().min(1).max(80),
    url: FormLinkUrlSchema,
    description: z.string().trim().max(160).default(""),
    icon: FormLinkIconSchema,
    embedAppId: FormEmbedAppIdSchema.optional(),
});
export type UpdateFormLinkInput = z.infer<typeof UpdateFormLinkInputSchema>;

/** Boundary input for `removeFormLink`. */
export const RemoveFormLinkInputSchema = z.object({
    id: z.string().min(1).transform(ExternalFormLinkId),
});
export type RemoveFormLinkInput = z.infer<typeof RemoveFormLinkInputSchema>;

/**
 * Boundary input for `reorderFormLinks` — the full ordered id list. The service
 * asserts it is a permutation of the current ids (no missing/duplicate/extra), so
 * a stale client can never drop a link or corrupt the order.
 */
export const ReorderFormLinksInputSchema = z.object({
    orderedIds: z.array(z.string().min(1).transform(ExternalFormLinkId)).min(1),
});
export type ReorderFormLinksInput = z.infer<typeof ReorderFormLinksInputSchema>;

/**
 * The seeded form list — named shortcuts to the kmu-trexpert customer portal.
 * All links point to {@link KUNDENPORTAL_URL} from day one; a TrexpertAdmin can
 * edit individual entries once the PostgreSQL adapter lands. German prose uses
 * correct Umlauts and `ss`.
 */
export const DEFAULT_FORM_LINKS: readonly ExternalFormLink[] = [
    {
        id: ExternalFormLinkId("mitarbeitende-anmelden"),
        label: "Mitarbeitende anmelden",
        url: KUNDENPORTAL_URL,
        description: "Neue Mitarbeitende bei den Sozialversicherungen anmelden.",
        icon: "user-plus",
        embedAppId: "00276bb0-fcfe-4f7d-a0a8-4782fb6383b3",
        order: 0,
    },
    {
        id: ExternalFormLinkId("unfallmeldung"),
        label: "Unfallmeldung",
        url: KUNDENPORTAL_URL,
        description: "Einen Berufs- oder Nichtberufsunfall melden.",
        icon: "clipboard",
        embedAppId: "e4786e8c-ae02-45ce-b33f-2fb9d3afe7b1",
        order: 1,
    },
    {
        id: ExternalFormLinkId("krankheitsmeldung"),
        label: "Krankheitsmeldung",
        url: KUNDENPORTAL_URL,
        description: "Eine krankheitsbedingte Arbeitsunfähigkeit melden.",
        icon: "file-text",
        embedAppId: "a677c3b7-5d3d-403f-8e76-5b850ce20f4f",
        order: 2,
    },
    {
        id: ExternalFormLinkId("mutterschaftsentschaedigung"),
        label: "Antrag auf Mutterschaftsentschädigung",
        url: KUNDENPORTAL_URL,
        description: "Antrag auf Mutterschaftsentschädigung stellen.",
        icon: "calendar",
        embedAppId: "16971b08-f426-4dc6-9abf-35d1be7e6ccd",
        order: 3,
    },
    {
        id: ExternalFormLinkId("vaterschaftsentschaedigung"),
        label: "Antrag auf Vaterschaftsentschädigung",
        url: KUNDENPORTAL_URL,
        description: "Antrag auf Vaterschaftsentschädigung stellen.",
        icon: "calendar",
        embedAppId: "020a5bfe-748b-4dd6-ab3b-ba7c56dcdcfd",
        order: 4,
    },
    {
        id: ExternalFormLinkId("familienzulagen-anmelden"),
        label: "Familienzulagen anmelden",
        url: KUNDENPORTAL_URL,
        description: "Familienzulagen für Mitarbeitende anmelden.",
        icon: "wallet",
        embedAppId: "199af0f6-8cc6-415f-85c3-325ab06fbf87",
        order: 5,
    },
    {
        id: ExternalFormLinkId("aenderungen-mitarbeitende"),
        label: "Änderungen bei Mitarbeitenden",
        url: KUNDENPORTAL_URL,
        description: "Änderungen zu bestehenden Mitarbeitenden melden.",
        icon: "briefcase",
        embedAppId: "54388f9b-0e4f-4627-b4cb-66efebbe1dd2",
        order: 6,
    },
    {
        id: ExternalFormLinkId("mitarbeitende-abmelden"),
        label: "Mitarbeitende abmelden",
        url: KUNDENPORTAL_URL,
        description: "Austretende Mitarbeitende abmelden.",
        icon: "user-minus",
        embedAppId: "19c26f55-ba6a-4b89-966f-2252e1695a05",
        order: 7,
    },
];

/**
 * Same-origin route that serves an Elfsight embed page for one app-id. The portal opens it inside a
 * modal `<iframe>` so the widget renders in its own document (its own light DOM + relaxed CSP),
 * leaving the strict host CSP and the touchpoints' Shadow DOM untouched.
 *
 * Under the module base, because that is the only tail app-base's proxy forwards: it matches a
 * service by `/cds/<module>` and passes the rest through verbatim. The previous `/trexpert/embed/...`
 * was reachable standalone and unroutable through app-base — the iframe simply rendered blank, with
 * no request the module ever saw.
 */
export const FORM_EMBED_PATH = "/cds/trexpert-portal/embed/form";

/**
 * The embed URL for an Elfsight app-id, under `pathPrefix`.
 *
 * `pathPrefix` is what makes the URL correct in BOTH deployments, and it is why this is computed on
 * the SERVER: only the server knows which it is in. Standalone it is empty and the module serves
 * {@link FORM_EMBED_PATH} directly; under app-base it is `/api/proxy/app/<appId>`, the front door
 * that reaches this module — a value the browser has no way to derive.
 */
export const formEmbedUrl = (embedAppId: string, pathPrefix = ""): string =>
    `${pathPrefix}${FORM_EMBED_PATH}/${embedAppId}`;

/** A URL-safe slug derived from a label, for deriving a new link's id. */
export const slugifyLabel = (label: string): string =>
    label
        .toLowerCase()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60) || "link";
